import { IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import Icon from '@components/Icon/Icon';

import type { DiscernRowData } from '@utils/discern-transforms';

const messages = defineMessages({
  deleteChallenge: {
    id: 'discernSummary.deleteDiscernDocumentChallenge',
    defaultMessage: 'To delete <b>{ docName }</b>, enter the text DELETE',
  },
  deleteDocument: {
    id: 'confirmation.deleteDocument',
    defaultMessage: 'Delete Document',
  },
  delete: {
    id: 'confirmation.deletePhrase',
    defaultMessage: 'DELETE',
  },
  deleteDisable: {
    id: 'discernSummary.deleteDisable',
    defaultMessage: 'Can not delete {status} document',
  },
});

interface DeleteDiscernButtonProps {
  rowData: DiscernRowData;
  handleDeleteDocument: (id: string) => Promise<void>;
}

const DeleteDiscernButton: React.FC<DeleteDiscernButtonProps> = ({
  rowData,
  handleDeleteDocument,
}) => {
  const { formatMessage } = useIntl();

  const canDelete =
    rowData.status === 'New' || rowData.status === 'Preparation';

  const deleteButton = (
    <span
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <ConfirmationDialog
        challenge={{
          phrase: formatMessage(messages.delete),
          description: formatMessage(messages.deleteChallenge, {
            docName: rowData.filename,
          }),
          type: 'text',
        }}
        title={formatMessage(messages.deleteDocument)}
        onConfirm={async () => handleDeleteDocument(rowData.discernDocumentId)}
      >
        {({ handleOpen }) => (
          <IconButton
            disabled={!canDelete}
            onClick={(e) => {
              e.stopPropagation();
              handleOpen();
            }}
            sx={{
              opacity: canDelete ? 1 : 0.5,
              cursor: canDelete ? 'pointer' : 'not-allowed',
            }}
          >
            <Icon name="trash" size="sm" type="fas" />
          </IconButton>
        )}
      </ConfirmationDialog>
    </span>
  );

  if (!canDelete) {
    return (
      <Tooltip
        title={formatMessage(messages.deleteDisable, {
          status: rowData.status,
        })}
      >
        <span>{deleteButton}</span>
      </Tooltip>
    );
  }

  return deleteButton;
};

export default DeleteDiscernButton;
